import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'

// ── Singleton state — shared across all useUser() calls ──
let _user    = null
let _profile = null
let _loading = true
const _listeners = new Set()

function notify() {
  _listeners.forEach(fn => fn())
}

async function _fetchProfile(session) {
  if (!session) { _profile = null; notify(); return }
  try {
    const res = await fetch('/api/profile', {
      headers: { Authorization: `Bearer ${session.access_token}` }
    })
    if (res.ok) {
      _profile = await res.json(); notify()
    } else if (res.status === 401) {
      // Token expired or invalid — force logout
      console.warn('[useUser] Session expired (401), logging out')
      _user = null; _profile = null; notify()
      supabase.auth.signOut()
    }
  } catch (e) {
    console.error('[useUser] fetchProfile failed', e)
  }
}

// Init once at module level
let _initialized = false
function _init() {
  if (_initialized) return
  _initialized = true

  supabase.auth.getSession().then(({ data }) => {
    const s = data.session
    _user = s?.user ?? null
    _fetchProfile(s).finally(() => { _loading = false; notify() })
  })

  supabase.auth.onAuthStateChange((_event, session) => {
    _user = session?.user ?? null
    _fetchProfile(session)
  })
}

export function useUser() {
  _init()

  const [, rerender] = useState(0)

  useEffect(() => {
    const fn = () => rerender(n => n + 1)
    _listeners.add(fn)
    return () => _listeners.delete(fn)
  }, [])

  const logout = () => {
    _profile = null
    notify()
    supabase.auth.signOut()
  }

  const refreshProfile = async () => {
    const { data } = await supabase.auth.getSession()
    await _fetchProfile(data.session)
  }

  return {
    user:    _user,
    profile: _profile,
    loading: _loading,
    logout,
    refreshProfile,
  }
}